<script>
    import {_, number} from 'svelte-i18n';
    import Starburst from '../lib/Starburst.svelte';
    import Countdown from '../lib/Countdown.svelte';
    import ItemsListing from "./ItemsListing.svelte";
    import {collect_daily_subscription_route} from "../constants";
    import {getPackRarity, resourceDetails} from "../globals.js";
    import {scrollToPackId, subscriptions, endExpiredSession, isSessionExpired} from "../stores.js";

    import wssubdays7pack1 from '../assets/wssubdays7pack1.png?w=300&format=webp;jpg';
    import wssubdays7pack5 from '../assets/wssubdays7pack5.png?w=300&format=webp;jpg';
    import wssubdays14pack10 from '../assets/wssubdays14pack10.png?w=300&format=webp;jpg';
    import stpatrickwebshop20 from '../assets/stpatrickwebshop20.png?w=300&format=webp;jpg';
    import stpatrickwebshop300 from '../assets/stpatrickwebshop300.png?w=300&format=webp;jpg';
    import SnowDecoration from "./SnowDecoration.svelte";

    let { subscription = {}, globals = {}, compact = false } = $props();

    let processing = $state(false);
    let animate = $state(false);
    let packEl = $state(null);
    // svelte-ignore state_referenced_locally
    let collected = $state(subscription.collected);

    $effect(() => {
        if ($scrollToPackId === subscription.packId && packEl) {
            scrollToPackId.set(null);
            setTimeout(() => {
                packEl?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setTimeout(() => {
                    animate = true;
                    setTimeout(() => { animate = false; }, 1000);
                }, 500);
            }, 0);
        }
    });
    let rarity = $derived(subscription.rarity);
    let goldBonus = $derived(subscription.goldBonus || '');
    let goldWorth = $derived(subscription.goldWorth || '');
    let goldAmount = $derived(subscription.rewards.gold);

    let headerImage = $derived(getHeaderImage());

    function getNextMidnightTime() {
        let midnight = new Date()
        midnight.setHours(24);
        midnight.setMinutes(0);
        midnight.setSeconds(0);
        midnight.setMilliseconds(0);

        return midnight.getTime()
    }

    // let collectedAt = new Date(collectedAt);
    // let remaining = Math.abs(getNextMidnightTime() - collectedAt);
    // let endDate = new Date(new Date().getTime() + remaining);

    function formatTimeFriendly(seconds) {
        let minutes = Math.floor(seconds / 60);
        let hours = Math.floor(minutes / 60);
        let days = Math.floor(hours / 24);

        let timeString = `${minutes} ${$_('Minute')}`;

        if (seconds - 1 > 60 * 60 * 24) {
            timeString = `${days} ${$_('Days')}`;
        } else if (seconds > 60 * 60) {
            timeString = `${hours} ${$_('Hours')}`;
        } else if (seconds >= 60) {
            timeString = `${minutes} ${$_('Minutes')}`;
        }

        return timeString;
    }

    // svelte-ignore state_referenced_locally
    globals.resourceDetails = resourceDetails;
    // svelte-ignore state_referenced_locally
    globals.formatTimeFriendly = formatTimeFriendly;

    function getHeaderImage() {
        switch (subscription.bundleName) {
            case 'wssubdays7pack1':
                return wssubdays7pack1;
            case 'wssubdays7pack5':
                return wssubdays7pack5;
            case 'wssubdays14pack10':
                return wssubdays14pack10;
            case 'stpatrickwebshop20':
                return stpatrickwebshop20;
            case 'stpatrickwebshop300':
                return stpatrickwebshop300;
            case 'newplayergiftsub1':
                return wssubdays7pack1;
            case 'returnpack5':
                return wssubdays7pack5;
            default:
                return '';
        }
    }

    // Mirror the local `collected` flag back into the subscriptions store so
    // reactive consumers (e.g. the tab notification dot) recompute. Matches the
    // array element by reference — the prop is the same object held in the store.
    function syncCollectedToStore() {
        subscriptions.update((list) => list.map((s) => (s === subscription ? { ...s, collected: true } : s)));
    }

    async function collectDailySubscription() {

        try {
            processing = true;
            const response = await fetch(collect_daily_subscription_route, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    packId: subscription.packId,
                }),
            });

            // web-shop's 403 is the LoggedInGuard, the only thing on this route that answers it: the
            // session is gone. It used to be read as "already collected", which showed the player a
            // reward they never got (#1349's session); clear the session so <Login/> takes over.
            if (isSessionExpired(response)) {
                endExpiredSession(collect_daily_subscription_route);
                return;
            }

            if (!response.ok) {
                throw new Error(`Collect subscription failed: ${response.status}`);
            }

            const res = await response.text();
            animate = true;
            collected = true;
            syncCollectedToStore();

        } catch (error) {
            if (error instanceof TypeError) {
                Rollbar.info('Collect subscription network failure: ' + error);
            } else {
                Rollbar.error('Error collecting daily subscription: ' + error);
            }
        } finally {
            processing = false;
        }
    }

    let packRarity = $derived(getPackRarity(rarity));
</script>

{#if compact}
<div class="pack-compact free {packRarity}">
    {#if subscription.showBonus}
        <Starburst>
            {$number(Math.floor((goldWorth + goldAmount + goldBonus) / (goldAmount + goldBonus)) * 100)}%
        </Starburst>
    {/if}
    {#if headerImage}
        <div class="pc-art">
            <img class="pack-image" src={headerImage[0]} alt={$_(subscription.bundleName + 'Title')}/>
            <!-- Title on the art like the paid packs' compact cards. -->
            <div class="pc-arttitle">
                <h3 class="pc-title">{$_(subscription.bundleName + 'Title')}</h3>
            </div>
        </div>
    {/if}
    <div class="pc-banner">
        {#if !headerImage}
            <h3 class="pc-title">{$_(subscription.bundleName + 'Title')}</h3>
        {/if}
        <!-- The desktop card's collect-daily summary in the banner band. -->
        <p class="pc-freedesc">
            {@html $_('SubscriptionDescription', {
                values: {
                    DAYS: '<b>' + subscription.rewards.days + '</b>',
                    GOLD_VALUE1: '<b>' + $number(subscription.rewards.gold * subscription.rewards.days) + '</b>',
                    GOLD_VALUE2: '<b>' + $number(goldWorth * subscription.rewards.days) + '</b>',
                },
            })}
        </p>
    </div>
    <!-- Items glance strip in the card body, matching the paid packs' layout
         (it used to sit on the art, hiding the gift image). -->
    <div class="pc-items">
        <ItemsListing globals={globals} goldAmount={goldAmount} items={subscription.rewards.items} storePack={{}} hasMask={false}/>
    </div>
    {#if collected}
        <button class="button primary claim-button green pc-collect" disabled>
            <SnowDecoration/>
            {$_('Collected')}
        </button>
    {:else}
        {#if subscription.finishAt}
            <Countdown endDate={subscription.finishAt}>
                {$_('IncrementalPacksTimeLeft')}
            </Countdown>
        {/if}
        <button class="button primary claim-button green pc-collect" class:loading={processing} disabled={processing} onclick={collectDailySubscription}>
            <SnowDecoration/>
            {$_('Collect')}
        </button>
    {/if}
</div>
{:else}
<div bind:this={packEl} id="pack-{subscription.packId}" class="pack free {collected ? '': 'active'} {packRarity} promos animate__animated {animate ? 'animate__rubberBand' : ''}">
    {#if subscription.showBonus}
        <Starburst>
            {$number(Math.floor((goldWorth + goldAmount + goldBonus) / (goldAmount + goldBonus)) * 100)}%
        </Starburst>
    {/if}
    <div class="content">
        <div class="header">
            <h2>{$_(subscription.bundleName + 'Title')}</h2>
            <span class="shadow"></span>
            <picture>
                <source srcset={headerImage[0]} type="image/webp">
                <source srcset={headerImage[1]} type="image/jpg">
                <img alt={$_(subscription.bundleName + 'Title')} class="pack-image" src={headerImage[0]}>
            </picture>
        </div>
        <div class="sub-header">
            <p>
                {@html $_('SubscriptionDescription', {
                    values: {
                        DAYS: '<b>' + subscription.rewards.days + '</b>',
                        GOLD_VALUE1: '<b>' + $number(subscription.rewards.gold * subscription.rewards.days) + '</b>',
                        GOLD_VALUE2: '<b>' + $number(goldWorth * subscription.rewards.days) + '</b>',
                    },
                })}
            </p>
        </div>

        <ItemsListing globals={globals} goldAmount={goldAmount} items={subscription.rewards.items} storePack={{}}/>
    </div>
    {#if collected}
        <span class="timer">{$_('SubscriptionPackCollectPopupInfo')}</span>
        <button class="button primary claim-button green" disabled>
            <SnowDecoration/>
            {$_('Collected')}
        </button>
    {:else}
        {#if subscription.finishAt}
            <Countdown endDate={subscription.finishAt}>
                {$_('IncrementalPacksTimeLeft')}
            </Countdown>
        {/if}
        <button class="button primary claim-button green" class:loading={processing} disabled={processing} onclick={collectDailySubscription}>
            <SnowDecoration/>
            {$_('Collect')}
        </button>
    {/if}
</div>
{/if}

<style lang="scss">
  @use '../styles/compact-card.scss' as compact;

  :global {
    .pack {
      &.free {
        &.active {
          --border-width: 8px;
          z-index: auto;
          //border: none;
          //width: calc(100% - var(--border-width) * 2);
          //height: calc(100% - var(--border-width) * 2);

          &.legendary {
            background: hsl(46deg 100% 71% / 80%);

            .sub-header {
              background: linear-gradient(
                              60deg,
                              hsl(224, 85%, 66%),
                              hsl(269, 85%, 66%),
                              hsl(314, 85%, 66%),
                              hsl(0, 85%, 66%),
                              hsl(27, 70%, 56%),
                              hsl(34, 85%, 56%),
                              hsl(41, 85%, 56%),
                              hsl(36, 94%, 67%)
              );
              background-size: 300% 300%;
              background-position: 0 100%;
              animation: moveGradient 10s alternate infinite;
            }
          }

          &::after {
            position: absolute;
            content: "";
            top: calc(-1 * var(--border-width));
            left: calc(-1 * var(--border-width));
            z-index: -1;
            width: calc(100% + var(--border-width) * 2);
            height: calc(100% + var(--border-width) * 2);
            background: linear-gradient(
                            60deg,
                            hsl(224, 85%, 66%),
                            hsl(269, 85%, 66%),
                            hsl(314, 85%, 66%),
                            hsl(359, 85%, 66%),
                            hsl(44, 85%, 66%),
                            hsl(89, 85%, 66%),
                            hsl(134, 85%, 66%),
                            hsl(179, 85%, 66%)
            );
            background-size: 300% 300%;
            background-position: 0 50%;
            border-radius: calc(2 * var(--border-width));
            animation: moveGradient 10s alternate infinite;
            filter: blur(3px);
          }

          @keyframes moveGradient {
            50% {
              background-position: 100% 50%;
            }
          }

          &:nth-child(1):after, &:nth-child(1) .sub-header {
            animation-delay: .1s;
          }

          &:nth-child(2):after, &:nth-child(2) .sub-header {
            animation-delay: 1s;
          }

          &:nth-child(3):after, &:nth-child(3) .sub-header {
            animation-delay: .5s;
          }
        }


        .sub-header {
          p {
            font-size: 13px;
          }
        }

        .claim-button {
          &.green {
            height: 52px;
          }
        }
      }
    }

  }

  @include compact.compact-card-styles;
</style>
